import React, { useCallback, useEffect, useRef, useState } from "react";
import { HKPImage } from "../../../media/HKPImage";
import { PlSheet } from "../list/ProductListBits";
import { approveShadePhoto, fetchShadePhotos, rejectShadePhoto, revokeShadeLink, useShadeAsMain } from "./shadeApi";
import type { ShadeList, ShadePhoto } from "./shadeApi";
import { SHADE_STRINGS as S } from "./shadeStrings";
import "./shade-panel.scss";

type Props = {
  productId: string;
  role?: string;
  /** Bumped when a new upload link was made elsewhere, so the open-links line refreshes. */
  refreshKey?: number;
  /** A decision changed the product (request status, or the main photos): reload it. */
  onChanged?: (what: "review" | "main") => void;
};

const day = (iso?: string | null) =>
  iso ? new Date(iso).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit", timeZone: "Asia/Kolkata" }) : "";

/**
 * Shade photos on /product/:id, under the Photos & dye card: pending first, then approved, then a collapsed
 * rejected group. Studio admins approve, reject and pick "Use as main photo" (one confirm with a preview);
 * everyone else sees them read-only. Tiles use the 480 copy, the viewer the 1200 copy, and the 2000 copy loads
 * only when Zoom is pressed. Renders nothing while there are no shade photos or open links.
 */
const ShadePhotosPanel = ({ productId, role, refreshKey = 0, onChanged }: Props) => {
  const [data, setData] = useState<ShadeList | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [showRejected, setShowRejected] = useState(false);
  const [viewer, setViewer] = useState<{ list: ShadePhoto[]; index: number } | null>(null);
  const [zoomed, setZoomed] = useState(false);
  const [confirm, setConfirm] = useState<ShadePhoto | null>(null);
  const [notice, setNotice] = useState("");
  const touchX = useRef<number | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await fetchShadePhotos(productId);
      if (res?.success && res.data) setData(res.data);
    } catch {
      // The panel stays hidden when it can't load; the rest of the page works.
    }
  }, [productId]);

  useEffect(() => {
    void load();
  }, [load, refreshKey]);

  const canReview = Boolean(data?.canReview) && role === "ORGANIZATION_ADMIN";

  const act = async (call: () => Promise<any>, what: "review" | "main", after?: () => void) => {
    setBusy(true);
    setError("");
    try {
      const res = await call();
      if (res?.success && res.data) {
        setData(res.data);
        after?.();
        onChanged?.(what);
      } else setError(res?.message || S.error);
    } catch {
      setError(S.noConnection);
    } finally {
      setBusy(false);
    }
  };

  const move = (step: number) => {
    setZoomed(false);
    setViewer((v) => (v ? { ...v, index: (v.index + step + v.list.length) % v.list.length } : v));
  };

  // Viewer keys: arrows move, Esc is handled by the sheet.
  useEffect(() => {
    if (!viewer) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowRight") move(1);
      if (e.key === "ArrowLeft") move(-1);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewer]);

  if (!data) return null;
  const { pending, approved, rejected } = data;
  const links = data.links || [];
  if (!pending.length && !approved.length && !rejected.length && !links.length) return null;

  const actions = (p: ShadePhoto, inViewer = false) => {
    if (!canReview) return p.state === "pending" ? <span className="pu-dim">{S.waitingAdmin}</span> : null;
    return (
      <div className="sp-acts">
        {p.state !== "approved" ? (
          <button type="button" className="pl-btn is-primary is-sm" disabled={busy} onClick={() => void act(() => approveShadePhoto(productId, p.id), "review", () => inViewer && setViewer(null))}>
            {S.approve}
          </button>
        ) : p.promotedToMainAt ? (
          <span className="sp-tag">{S.isMain}</span>
        ) : (
          <button type="button" className="pl-btn is-primary is-sm" disabled={busy} onClick={() => setConfirm(p)}>
            {S.useAsMain}
          </button>
        )}
        {p.state !== "rejected" ? (
          <button type="button" className="pl-btn is-ghost is-sm" disabled={busy} onClick={() => void act(() => rejectShadePhoto(productId, p.id), "review", () => inViewer && setViewer(null))}>
            {S.reject}
          </button>
        ) : null}
      </div>
    );
  };

  const grid = (list: ShadePhoto[]) => (
    <ul className="sp-grid">
      {list.map((p, index) => (
        <li key={p.id} className={`sp-tile is-${p.state}`}>
          <button type="button" className="sp-img" onClick={() => { setZoomed(false); setViewer({ list, index }); }} aria-label={`Open shade photo ${index + 1}`}>
            {p.card || p.thumbnail ? (
              <HKPImage preset="PRODUCT_CARD" legacyUrl={(p.card || p.thumbnail) as string} alt={`Shade photo ${index + 1}${p.shade ? `, ${p.shade}` : ""}`} sizes="(max-width: 600px) 33vw, 160px" />
            ) : (
              <span className="sp-wait">{S.copiesPending}</span>
            )}
            {p.isPrimary ? <span className="sp-badge">{S.primary}</span> : null}
          </button>
          <span className="sp-meta">{S.sentBy(p.uploadedBy, day(p.takenAt))}</span>
          {actions(p)}
        </li>
      ))}
    </ul>
  );

  const current = viewer ? viewer.list[viewer.index] : null;

  return (
    <div className="vp-card sp-panel" id="shade-photos">
      <div className="vp-panel__head">
        <span className="vp-card__title">{S.panelTitle}</span>
      </div>
      <p className="pu-dim">{S.panelHint}</p>

      {pending.length ? (
        <section aria-label={S.pending(pending.length)}>
          <h4 className="sp-h">{S.pending(pending.length)}</h4>
          {grid(pending)}
        </section>
      ) : null}

      {approved.length ? (
        <section aria-label={S.approved(approved.length)}>
          <h4 className="sp-h">{S.approved(approved.length)}</h4>
          {grid(approved)}
        </section>
      ) : null}

      {rejected.length ? (
        <section>
          <button type="button" className="pu-link" aria-expanded={showRejected} onClick={() => setShowRejected((s) => !s)}>
            {showRejected ? S.hideRejected : S.rejected(rejected.length)}
          </button>
          {showRejected ? grid(rejected) : null}
        </section>
      ) : null}

      {canReview && links.length ? (
        <div className="sp-links">
          <span className="pu-dim">{S.openLinks(links.length)}</span>
          {links.map((l) => (
            <div key={l.id} className="sp-link">
              <span>
                {l.used} / {l.maxUploads} · {S.linkExpires(day(l.expiresAt))}
              </span>
              <button type="button" className="pl-btn is-ghost is-sm" disabled={busy} onClick={() => void act(() => revokeShadeLink(productId, l.id), "review")}>
                {S.closeLink}
              </button>
            </div>
          ))}
        </div>
      ) : null}

      {notice ? (
        <div className="sp-notice" role="status">
          {notice}
        </div>
      ) : null}
      {error ? (
        <div className="pu-error" role="alert">
          {error}
        </div>
      ) : null}

      <PlSheet open={Boolean(current)} onClose={() => setViewer(null)} label={S.panelTitle} className="sp-viewer">
        {(close) =>
          current ? (
            <div className="sp-viewer__body">
              <div
                className={`sp-viewer__img${zoomed ? " is-zoomed" : ""}`}
                onTouchStart={(e) => (touchX.current = e.touches[0]?.clientX ?? null)}
                onTouchEnd={(e) => {
                  const start = touchX.current;
                  const end = e.changedTouches[0]?.clientX;
                  touchX.current = null;
                  if (start != null && end != null && Math.abs(end - start) > 55 && !zoomed) move(end < start ? 1 : -1);
                }}
              >
                {zoomed && current.zoom ? (
                  <HKPImage preset="ZOOM" legacyUrl={current.zoom} alt="Shade photo, zoomed" loading="eager" />
                ) : current.detail || current.card ? (
                  <HKPImage preset="PRODUCT_DETAIL" legacyUrl={(current.detail || current.card) as string} alt={`Shade photo${current.shade ? `, ${current.shade}` : ""}`} loading="eager" />
                ) : (
                  <span className="sp-wait">{S.copiesPending}</span>
                )}
              </div>
              <div className="sp-viewer__bar">
                {viewer && viewer.list.length > 1 ? (
                  <>
                    <button type="button" className="pl-btn is-ghost is-sm" aria-label={S.previous} onClick={() => move(-1)}>
                      ‹
                    </button>
                    <span className="pu-dim" aria-live="polite">
                      {viewer.index + 1} / {viewer.list.length}
                    </span>
                    <button type="button" className="pl-btn is-ghost is-sm" aria-label={S.next} onClick={() => move(1)}>
                      ›
                    </button>
                  </>
                ) : null}
                {current.zoom && !zoomed ? (
                  <button type="button" className="pl-btn is-ghost is-sm" onClick={() => setZoomed(true)}>
                    {S.zoom}
                  </button>
                ) : null}
              </div>
              {actions(current, true)}
              <button type="button" className="pl-btn is-ghost" onClick={close}>
                {S.close}
              </button>
            </div>
          ) : null
        }
      </PlSheet>

      <PlSheet open={Boolean(confirm)} onClose={() => setConfirm(null)} label={S.confirmTitle} className="pu-sheet">
        {(close) =>
          confirm ? (
            <div className="pu-sheet__body">
              <h3 className="pu-sheet__title">{S.confirmTitle}</h3>
              <div className="sp-preview">
                <div className="sp-preview__first">
                  {confirm.card ? <HKPImage preset="PRODUCT_CARD" legacyUrl={confirm.card} alt="New first main photo" loading="eager" /> : null}
                  <span className="sp-badge">1</span>
                </div>
                <p className="pu-dim">{S.confirmBody}</p>
              </div>
              <div className="pu-actions">
                <button
                  type="button"
                  className="pl-btn is-primary"
                  disabled={busy}
                  onClick={() =>
                    void act(() => useShadeAsMain(productId, confirm.id), "main", () => {
                      close();
                      setViewer(null);
                      setNotice(S.newMain);
                    })
                  }
                >
                  {S.confirm}
                </button>
                <button type="button" className="pl-btn is-ghost" onClick={close}>
                  {S.cancel}
                </button>
              </div>
            </div>
          ) : null
        }
      </PlSheet>
    </div>
  );
};

export default ShadePhotosPanel;
